import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Accordion,
  AngleDown,
  ChakraBox,
  ChakraFlex,
  ChakraSimpleGrid,
  ChakraStack,
  ChakraText,
  DateFormat,
  DatePicker,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  FAIconName,
  FireflyButton,
  Flag,
  Menu,
  RadioGroup,
  TextAreaInput,
  TextInput,
} from '@equip.health/ui';

import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import {
  ACCORDION_HEIGHT_CALCULATED,
  ACCORDION_WIDTH_LARGE,
} from '~/lib/constants';
import { UNASSIGNED_USER } from '~/lib/constants/user.constants';
import { useTaskContext } from '~/lib/contexts/TaskContext';
import {
  filterDisabledSubCategories,
  filterInActiveSubCategories,
  invalidNameError,
  isValidName,
  TaskCategoryColorMapper,
  taskPriorityDefinitions,
} from '~/lib/util/task.util';

import AssigneeSelection from './AssigneeSelection';
import PatientSelection from './PatientSelection';
import { DateTime } from 'luxon';
import { useCreateDischargePlanTaskMutation } from '~/store/services/clinical-data/dischargePlan';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { Listeners } from '~/lib/constants/global.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { TaskEvents } from '~/analytics/taskEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  CREATE_TASK_SOURCES,
  CreateTaskSource,
} from '~/lib/constants/task.constants';

const TOP_LEVEL_ID = 'new-task';

type TaskErrors = {
  name: boolean;
  assignee: boolean;
  category: boolean;
  subcategory: boolean;
  dueDate: boolean;
};

interface NewTaskAccordionProps {
  triggerSource?: CreateTaskSource;
  defaultCategoryName?: string;
  defaultPatient?: Patient;
  dismissAddTask: () => void;
  inquiryId?: string;
  dischargePlanId?: string;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  showLinkedPatient: boolean;
}

const NewTaskAccordion = ({
  triggerSource,
  defaultCategoryName,
  defaultPatient,
  dismissAddTask,
  dischargePlanId,
  inquiryId,
  isExpanded,
  setIsExpanded,
  showLinkedPatient,
}: NewTaskAccordionProps) => {
  const defaults = {
    assignee: undefined,
    category: undefined,
    description: '',
    dueDate: DateTime.now().toFormat(DateFormat.longDate),
    name: '',
    patient: defaultPatient,
    subcategory: undefined,
    taskPriorityId: taskPriorityDefinitions[0].value,
  };

  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState<boolean>(false);

  // New task values
  const [name, setName] = useState<string>(defaults.name);
  const [description, setDescription] = useState<string>(defaults.description);
  const [dueDate, setDueDate] = useState<string>(defaults.dueDate);
  const [taskPriorityId, setTaskPriorityId] = useState<string>(
    defaults.taskPriorityId,
  );
  const [assignee, setAssignee] = useState<GetProviderByIdResponse | undefined>(
    defaults.assignee,
  );
  const [patient, setPatient] = useState<Patient | undefined>(defaults.patient);
  const [category, setCategory] = useState<Category | undefined>(
    defaults.category,
  );
  const [subcategory, setSubcategory] = useState<Category | undefined>(
    defaults.subcategory,
  );

  const [isDueDateValid, setIsDueDateValid] = useState<boolean>(false);
  const { trackEvent } = useAnalytics();
  const { listenerCallback } = useGlobalContext();
  const {
    categories,
    createTask,
    createTaskData,
    createTaskError,
    isCreateTaskProcessing,
    isUnsavedAddTaskChanges,
    setIsUnsavedAddTaskChanges,
    resetCreateTaskData,
    resetCreateTaskError,
  } = useTaskContext();

  const [
    createDischargePlanTasks,
    { isError: isDischargeTaskError, isLoading: isDischargeTaskLoading },
  ] = useCreateDischargePlanTaskMutation();
  useEffect(() => {
    if (categories && defaultCategoryName) {
      setCategory(
        categories.find(
          (category) => category.categoryName === defaultCategoryName,
        ),
      );
    }
  }, [categories, defaultCategoryName]);

  const handleStateFieldChange = <T,>(
    setState: Dispatch<SetStateAction<T>>,
    value: T,
  ): void => {
    resetCreateTaskError();
    setState(value);
    setIsUnsavedAddTaskChanges(true);
  };

  const handleNameChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    handleStateFieldChange(setName, e.target.value);
  };

  const handleDescriptionChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    handleStateFieldChange(setDescription, e.target.value);
  };

  const handleDueDateChange = (date: Date | null): void => {
    handleStateFieldChange(
      setDueDate,
      date ? DateTime.fromJSDate(date).toFormat(DateFormat.longDate) : '',
    );
  };

  const handlePriorityChange = (selected: string): void => {
    handleStateFieldChange(setTaskPriorityId, selected);
  };

  const resetCreateTaskDataAndError = () => {
    resetCreateTaskData();
    resetCreateTaskError();
  };

  useEffect(() => {
    resetCreateTaskDataAndError();
    return resetCreateTaskDataAndError;
  }, []);

  // Temporarily assign a default category color (remove once /tasks/categories API is ready)
  const taskCategories = useMemo(
    () =>
      (categories ?? []).map((category: Category) => ({
        ...category,
        categoryColor:
          category.categoryColor ??
          TaskCategoryColorMapper[
            category.categoryName.replace(/ /g, '_').toLowerCase()
          ],
      })),
    [categories?.length],
  );

  const allowedSubCategories = useMemo(
    () =>
      filterInActiveSubCategories(
        filterDisabledSubCategories(category?.subCategoryList ?? []),
      ),
    [category?.categoryExternalId],
  );

  const handleCategorySelect = (value: string): void => {
    handleStateFieldChange(
      setCategory,
      taskCategories.find(
        ({ categoryName }: Category) => categoryName === value,
      ),
    );
  };

  const handleSubcategorySelect = (value: string): void => {
    handleStateFieldChange(
      setSubcategory,
      category?.subCategoryList?.find(
        ({ categoryName }: Category) => categoryName === value,
      ),
    );
  };

  const onResetAndClose = (): void => {
    setIsUnsavedAddTaskChanges(false);
    setIsUnsavedChangesModalOpen(false);
    setAssignee(defaults.assignee);
    setCategory(defaults.category);
    setDescription(defaults.description);
    setDueDate(defaults.dueDate);
    setPatient(defaults.patient);
    setTaskPriorityId(defaults.taskPriorityId);
    setSubcategory(defaults.subcategory);
    setName(defaults.name);
    dismissAddTask();
  };

  const handleCancelAndClose = (): void => {
    if (isUnsavedAddTaskChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      onResetAndClose();
    }
  };
  const getAnalyticsEvent = (): TaskEvents => {
    switch (triggerSource) {
      case CREATE_TASK_SOURCES.CREATE_NEW_BUTTON:
        return TaskEvents.taskCreateConfirmClickedFromCreateNewButton;
      case CREATE_TASK_SOURCES.ACTION_MENU:
        return TaskEvents.taskCreateConfirmClickedFromActionMenu;
      case CREATE_TASK_SOURCES.INQUIRY_DETAIL:
        return TaskEvents.taskCreateConfirmClickedFromInquiryDetail;
      case CREATE_TASK_SOURCES.DISCHARGE_PLAN_VIEW:
        return TaskEvents.taskCreateConfirmClickedFromDischargePlanTaskPanel;
      case CREATE_TASK_SOURCES.TASKS_LIST_PANEL:
        return TaskEvents.taskCreateConfirmClickedFromTasksListPanel;
      default: {
        const _exhaustive: never = triggerSource;
        throw new Error(`Unhandled triggerSource: ${_exhaustive}`);
      }
    }
  };

  const handleTaskCreated = (taskExternalId: string): void => {
    trackEvent(getAnalyticsEvent(), {
      Source: AnalyticsSourceValues.TASK_FORM,
      patientId:
        patient?.userId === UNASSIGNED_USER.userId ? null : patient?.userId,
      taskName: name,
      taskId: taskExternalId,
    });
  };

  useEffect(() => {
    if (createTaskData?.taskExternalId) {
      handleTaskCreated(createTaskData.taskExternalId);
      onResetAndClose();
    }
  }, [createTaskData?.taskExternalId]);

  useEffect(() => {
    const newSubCategory = allowedSubCategories.some(
      ({ categoryExternalId }) =>
        categoryExternalId === subcategory?.categoryExternalId,
    )
      ? subcategory
      : (allowedSubCategories[0] ?? null);

    setSubcategory(newSubCategory);
  }, [category?.categoryName]);

  const taskErrors = useMemo<TaskErrors>(
    () => ({
      assignee: !assignee,
      category: !category,
      dueDate: !isDueDateValid,
      name: !isValidName(name),
      subcategory: !subcategory && !!allowedSubCategories.length,
    }),
    [assignee, category, isDueDateValid, name, subcategory],
  );

  const priorityOptions: SelectOption[] = useMemo(
    () =>
      taskPriorityDefinitions.map(({ color, title, value }) => ({
        label: (
          <ChakraBox alignItems="center" display="flex" fontSize="10px">
            &nbsp;
            <Flag fill={color} height="12px" width="12px" />
            &nbsp;
            {title}
          </ChakraBox>
        ),
        value,
      })),
    [],
  );

  const handleConfirmClick = (): void => {
    if (dischargePlanId) {
      createDischargePlanTasks({
        dischargePlanId,
        dueDate: DateTime.fromJSDate(new Date(dueDate)).toFormat(
          DateFormat.shortYearMonthDay,
        ),
        taskAssignedUserId:
          assignee?.userId === UNASSIGNED_USER.userId
            ? 'Unassigned'
            : assignee?.userId,
        taskCategoryExternalId: category?.categoryExternalId,
        taskDescription: description,
        taskName: name,
        taskPriorityId,
        taskStatusId: '1',
        taskSubCategoryExternalId: subcategory?.categoryExternalId,
      })
        .unwrap()
        .then((response) => {
          listenerCallback(
            [
              Listeners.PATIENTMENU_TASKDETAILS_CREATE,
              Listeners.PATIENTTASKS_TASKDETAILS_CREATE,
            ],
            [[], []],
          );
          handleTaskCreated(response.taskExternalId);
          onResetAndClose();
        })
        .catch((error) => {
          console.error('Error creating discharge plan task:', error);
        });

      return;
    }
    createTask({
      assignee: assignee?.userId === UNASSIGNED_USER.userId ? null : assignee,
      category,
      description,
      dueDate,
      inquiryId,
      name,
      patient: patient?.userId === UNASSIGNED_USER.userId ? null : patient,
      subcategory,
      taskPriorityId,
    });
  };

  const validateDueDate = (isValid: boolean): void => {
    setIsDueDateValid(!!dueDate && isValid);
  };

  const { primary } = EquipUIFireflyV2Theme.colors;
  const { label, paragraph } = EquipUITheme.typography;
  const footerContent = (
    <ChakraFlex alignItems="center" justifyContent="space-between" width="100%">
      {(createTaskError || isDischargeTaskError) && (
        <ChakraText {...label} color="critical.base" marginRight="16px">
          Error saving task
        </ChakraText>
      )}
      <FireflyButton
        id={`${TOP_LEVEL_ID}__cancel`}
        leftIcon={FAIconName.XmarkRegular}
        onClick={handleCancelAndClose}
        variant="secondary"
      >
        Cancel
      </FireflyButton>
      <FireflyButton
        id={`${TOP_LEVEL_ID}__confirm`}
        isDisabled={
          Object.values(taskErrors).some((val: boolean) => val) ||
          isCreateTaskProcessing ||
          isDischargeTaskLoading
        }
        leftIcon={FAIconName.checkRegular}
        onClick={handleConfirmClick}
      >
        Confirm
      </FireflyButton>
    </ChakraFlex>
  );

  const requireSubcategory = !!allowedSubCategories.length;

  return (
    <>
      <DiscardChangesModalV2
        isOpen={isUnsavedChangesModalOpen}
        onClose={() => setIsUnsavedChangesModalOpen(false)}
        onConfirm={onResetAndClose}
      />
      <Accordion
        dismissMenu={handleCancelAndClose}
        footer={footerContent}
        id={TOP_LEVEL_ID}
        isExpanded={isExpanded}
        panelContentMaxHeight={ACCORDION_HEIGHT_CALCULATED}
        panelMaxWidth={ACCORDION_WIDTH_LARGE}
        setIsExpanded={setIsExpanded}
        title="Add task"
      >
        <ChakraFlex minWidth={ACCORDION_WIDTH_LARGE} padding="32px">
          <ChakraStack spacing="16px" width="100%">
            <PatientSelection
              autoFillPatient={showLinkedPatient}
              id="new-task"
              // If inquiryId and patient are valid we want to disable changing the patient.
              isDisabled={!!((inquiryId || dischargePlanId) && patient)}
              patient={patient}
              updatePatient={(newPatient: Patient) =>
                handleStateFieldChange(setPatient, newPatient)
              }
            />
            <TextInput
              errorMessage={invalidNameError}
              id={`${TOP_LEVEL_ID}__title`}
              isInvalid={name && taskErrors.name}
              isRequired
              label="Task title"
              onChange={handleNameChange}
              value={name}
            />
            <TextAreaInput
              fontSize="14px"
              id={`${TOP_LEVEL_ID}__description`}
              label="Task description"
              onChange={handleDescriptionChange}
              value={description}
            />
            <AssigneeSelection
              assignee={assignee}
              id="new-task"
              updateAssignee={(
                newAssignee: Partial<GetProviderByIdResponse> | undefined,
              ) => {
                handleStateFieldChange(
                  setAssignee,
                  (newAssignee ?? null) as GetProviderByIdResponse | null,
                );
              }}
            />
            <ChakraSimpleGrid columns={2} spacing={3}>
              <ChakraBox display="flex" flexDirection="column" width="100%">
                <Menu
                  {...paragraph}
                  backgroundColor="neutral.background.secondary"
                  border="none"
                  buttonContent={
                    !category ? undefined : (
                      <ChakraBox alignItems="center" display="flex">
                        <ChakraBox
                          background={category.categoryColor}
                          borderRadius="50px"
                          height="8px"
                          marginLeft="6px"
                          marginRight="22px"
                          width="8px"
                        />
                        <ChakraText color="neutral.primary">
                          {category.categoryName}
                        </ChakraText>
                      </ChakraBox>
                    )
                  }
                  color="neutral.secondary"
                  icon={<AngleDown fill={primary[100]} fillOpacity={1} />}
                  id={`${TOP_LEVEL_ID}__category`}
                  isFullWidth
                  isRequired
                  label="Category"
                  listMaxHeight="180px"
                  onSelect={handleCategorySelect}
                  options={(categories ?? []).map(
                    ({ categoryColor, categoryName }) => ({
                      node: (
                        <ChakraBox alignItems="center" display="flex">
                          <ChakraBox
                            background={
                              categoryColor ??
                              TaskCategoryColorMapper[
                                categoryName.replace(/ /g, '_').toLowerCase()
                              ]
                            }
                            borderRadius="50px"
                            height="8px"
                            marginLeft="6px"
                            marginRight="22px"
                            width="8px"
                          />
                          <ChakraText
                            {...paragraph}
                            id={`${TOP_LEVEL_ID}__category-option__${categoryName}`}
                          >
                            {categoryName}
                          </ChakraText>
                        </ChakraBox>
                      ),
                      value: categoryName,
                    }),
                  )}
                  overflow="scroll"
                  placeholder="Select one option"
                  textAlign="left"
                />
              </ChakraBox>
              <ChakraBox display="flex" flexDirection="column" width="100%">
                <Menu
                  {...paragraph}
                  backgroundColor="neutral.background.secondary"
                  border="none"
                  buttonContent={
                    !subcategory ? undefined : (
                      <ChakraText color="black">
                        {subcategory.categoryName}
                      </ChakraText>
                    )
                  }
                  color="neutral.secondary"
                  icon={<AngleDown fill={primary[100]} fillOpacity={1} />}
                  id={`${TOP_LEVEL_ID}__subcategory`}
                  isDisabled={!requireSubcategory}
                  isFullWidth
                  isRequired={requireSubcategory}
                  label="Sub-category"
                  listMaxHeight="300px"
                  onSelect={handleSubcategorySelect}
                  options={allowedSubCategories
                    .filter(({ isSelectable }) => isSelectable !== false)
                    .map(({ categoryName }) => ({
                      value: categoryName,
                      node: (
                        <ChakraText
                          {...paragraph}
                          id={`${TOP_LEVEL_ID}__subcategory-option__${categoryName}`}
                        >
                          {categoryName}
                        </ChakraText>
                      ),
                    }))}
                  overflow="scroll"
                  placeholder="Select one option"
                  textAlign="left"
                />
              </ChakraBox>
            </ChakraSimpleGrid>
            <DatePicker
              color="neutral.secondary"
              id={`${TOP_LEVEL_ID}__due-date`}
              isRequired
              label="Due date"
              minDate={new Date()}
              onSelect={handleDueDateChange}
              onValidateDate={validateDueDate}
              placeHolderText="MM/DD/YYYY"
              value={new Date(dueDate)}
            />
            <RadioGroup
              direction="row"
              id={`${TOP_LEVEL_ID}__priority`}
              onChange={handlePriorityChange}
              options={priorityOptions}
              overflow="hidden"
              radioOptionBorderRadius="16px"
              title="Priority"
              value={taskPriorityId}
            />
          </ChakraStack>
        </ChakraFlex>
      </Accordion>
    </>
  );
};

export default NewTaskAccordion;
